import React, { useEffect, useState, useRef, useImperativeHandle, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import useShowPDF from "@util/hook/useShowPDF";
import { NavLink } from "react-router-dom";
import { Grid } from "@mui/material";
import { Alert, Button, Card, CheckboxField, DateField, MenuItem, SelectField, TextField, Box, Typography, Stack, Form, RadioField } from "@curves/ui-base";
import { ReadTextField, SignBox, EmployeeSelect, PageLayout } from "@icoach/components/";
import { isEqual, refIsRequiredError } from "@util/utils";
import { DATE_TIME_STANDARD } from "@icoach/constants/appConfig";
import { parseDate, getLastOfMonth } from "@util/moment";
import { FieldGroup, FieldRow } from "@icoach/smartFitnessBand/components/DocumentGridLayout";
import useDeviceOrderApi from "@apis/useDeviceOrderApi";
import useAppSystemApi from "@apis/useAppSystemApi";
import { CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";

// 合約方案 - 申請日與有效日計算組件
const TerminationDatePanel = React.forwardRef((props, ref) => {
	const [toDay, setToDay] = useState(null);
	const [applyDate, setApplyDate] = useState(null);
	const [period, setPeriod] = useState(null);
	const applyDateRef = useRef(null);
	const periodRef = useRef(null);
	const { getTaipeiTimeApi } = useAppSystemApi();

	const handleRadioSwitch = (_e, value) => {
		setPeriod(value);
	};

	const handleDateChage = (_dom, value) => {
		setPeriod(null);
		setApplyDate(value);
	};

	const isCurrentMiddle = useCallback(
		() => {
			if (applyDate === null) return false;
			const selectedMonth = parseDate(applyDate, "MM");
			const currentMonth = parseDate(toDay, "MM");
			if (isEqual(selectedMonth, currentMonth)) {
				const day = parseDate(toDay, "DD");
				return parseInt(day) > 15; // 月中
			} else {
				return false;
			}
		},
		// eslint-disable-next-line
		[applyDate]
	);

	const endEffectDate = useCallback(
		() => {
			if (applyDate === null) return applyDate;
			if (isEqual(period, 0)) return parseDate(applyDate, `${DATE_TIME_STANDARD.DISPLAYYEARMONTH}-15`);
			else if (isEqual(period, 1)) return parseDate(getLastOfMonth(applyDate), DATE_TIME_STANDARD.DISPLAYDATE);
			else return null;
		},
		// eslint-disable-next-line
		[period]
	);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					applyDate: toDay,
					effectDate: endEffectDate(),
				};
			},
			isError: () => {
				return refIsRequiredError(applyDateRef, periodRef);
			},
		}),
		// eslint-disable-next-line
		[endEffectDate]
	);

	useEffect(
		() => {
			const fetchTime = async () => {
				const resp = await getTaipeiTimeApi();
				if (resp) {
					const t = parseDate(resp.dateTime);
					setToDay(t);
					setApplyDate(t);
				}
			};
			fetchTime();
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<React.Fragment>
			<Grid item xs={6}>
				<DateField
					ref={applyDateRef}
					label="終止申請月"
					key={toDay}
					defaultValue={toDay}
					onChange={handleDateChage}
					minDate={toDay}
					views={["year", "month"]}
					openTo="month"
					format="YYYY/MM"
					mask="____/__"
					fullWidth
					required
				/>
			</Grid>
			<Grid item xs={6}>
				<RadioField ref={periodRef} key={applyDate} label={"區段"} onChange={handleRadioSwitch} fullWidth required row>
					<RadioField.Radio value={0} label={"月中"} disabled={isCurrentMiddle()} />
					<RadioField.Radio value={1} label={"月底"} />
				</RadioField>
			</Grid>
			<Grid item xs={6}>
				<ReadTextField label="終止申請日期">{toDay}</ReadTextField>
			</Grid>
			<Grid item xs={6}>
				<ReadTextField label="終止生效日期">{endEffectDate()}</ReadTextField>
			</Grid>
		</React.Fragment>
	);
});

// 合約方案
const ContractQuitPlan = ({ sourceData }) => {
	const { payment, effectCreateDate, suspendMonth, memberPaymentTypeText, deviceSubscriptionPrice, memberCardNo } = sourceData;
	const { creditCardNo } = payment;
	return (
		<React.Fragment>
			<Card.CardHeader title="合約方案" className="bg-light text-center" />
			<Card.CardContent className="px-4 py-5">
				<Grid container spacing={3}>
					<Grid item xs={6}>
						<ReadTextField label="付款方式">{memberPaymentTypeText}</ReadTextField>
					</Grid>
					<Grid item xs={6}>
						<ReadTextField label="信用卡卡號">{creditCardNo}</ReadTextField>
					</Grid>
					<Grid item xs={6}>
						<ReadTextField label="月租定價">{deviceSubscriptionPrice}</ReadTextField>
					</Grid>
					<Grid item xs={6}>
						<ReadTextField label="電子簽到卡編號">{memberCardNo}</ReadTextField>
					</Grid>
					<Grid item xs={6}>
						<ReadTextField label="啟用日期">{parseDate(effectCreateDate)}</ReadTextField>
					</Grid>
					<Grid item xs={6}>
						<ReadTextField label="暫停月數">{suspendMonth}</ReadTextField>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
};

// 填寫表單
const ContractQuitFormFields = React.forwardRef(({ sourceData }, ref) => {
	const { resource = { quitReasonOptions: [], employeeOptions: [] } } = sourceData || {};
	const { quitReasonOptions = [], employeeOptions = [] } = resource || {};
	const terminationDatePanelRef = useRef(null);
	const employeeIDRef = useRef(null);
	const quitReasonTypeRef = useRef(null);
	const quitReasonNoteRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				const t = terminationDatePanelRef.current.getResult();
				return {
					...t,
					employeeID: employeeIDRef.current.getResult(),
					unsubscribeReason: quitReasonTypeRef.current.getResult(), // 暫時先用unsubscribeReason 要改成 quitReasonType
					reasonText: quitReasonNoteRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(terminationDatePanelRef, employeeIDRef, quitReasonTypeRef),
		}),
		[]
	);

	return (
		<React.Fragment>
			<Card.CardHeader title="填寫表單" className="bg-light text-center" />
			<Card.CardContent className="px-4 py-5">
				<FieldGroup label="表單資訊">
					<TerminationDatePanel ref={terminationDatePanelRef} />
					<Grid item xs={6}>
						<EmployeeSelect ref={employeeIDRef} options={employeeOptions} label="經辦教練" fullWidth required />
					</Grid>
				</FieldGroup>
				<FieldGroup label="意見回饋">
					<Grid item xs={8}>
						<SelectField ref={quitReasonTypeRef} label="終止原因" displayEmpty fullWidth required>
							{quitReasonOptions.map(options => (
								<MenuItem key={options.value} value={options.value}>
									{options.text}
								</MenuItem>
							))}
						</SelectField>
					</Grid>
					<Grid item xs={8}>
						<TextField ref={quitReasonNoteRef} label="誠摯邀請您對於店舖服務，提供寶貴的意見" multiline fullWidth rows={4} />
					</Grid>
				</FieldGroup>
			</Card.CardContent>
		</React.Fragment>
	);
});

// 費用計算 - 文字說明
const ContractPayableNote = props => {
	return (
		<Stack direction="column" spacing="2">
			<Typography component="span" variant="h6">
				1.會員如於當月15日(含)前提出終止申請，得申請當月份系統使用費之50%(新台幣125元)退款。
			</Typography>
			<Typography component="span" variant="h6">
				2.退款依公司指定線上申請辦理程序。
			</Typography>
            <Typography component="span" variant="h6">
				3.會員若終止本訂閱服務，並欲重新訂閱，將視為新契約關係，適用所有初次簽約之費用與條件，包括開通設定費。原契約與新契約視為各自獨立契約，先前支付之費不予轉移或抵扣。
			</Typography>
		</Stack>
	);
};

// 費用計算
const ContractQuitPayableInfo = React.forwardRef(({ sourceData }, ref) => {
	const { subscriptionStartDate, subscriptionMonth, usedMonth, suspendMonth } = sourceData || {};
	const checkBox1_Ref = useRef(null);
	const isAllowCheckedRef = useRef(null);
	const signRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				const items_1 = checkBox1_Ref.current.getResult().map(x => x.checked);
				return {
					survey: { items: [...items_1] },
					signImg: signRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(checkBox1_Ref, isAllowCheckedRef, signRef),
		}),
		[]
	);

	return (
		<React.Fragment>
			<Card.CardHeader title="費用計算" className="bg-light text-center" />
			<Card.CardContent className="px-4 py-5">
				<FieldGroup label="退費規則與流程">
					<Grid item xs={12}>
						<CheckboxField ref={checkBox1_Ref} required>
							<CheckboxField.Checkbox label={<ContractPayableNote />} />
						</CheckboxField>
					</Grid>
				</FieldGroup>
				<FieldGroup label="使用期間">
					<FieldRow alignLabel="left" items={[3, 9]} label="訂閱起始日期">
						<ReadTextField>{parseDate(subscriptionStartDate)}</ReadTextField>
					</FieldRow>
					<FieldRow alignLabel="left" items={[3, 9]} label="訂閱期間月份">
						<ReadTextField>{subscriptionMonth}</ReadTextField>
					</FieldRow>
					<FieldRow alignLabel="left" items={[3, 9]} label="實際使用月份">
						<ReadTextField>{usedMonth}</ReadTextField>
					</FieldRow>
					<FieldRow alignLabel="left" items={[3, 9]} label="暫停月份">
						<ReadTextField>{suspendMonth}</ReadTextField>
					</FieldRow>
				</FieldGroup>
				<Box className="mt-5">
					<FieldGroup label="注意事項">
						<Grid item xs={12}>
							<Alert severity="info" variant="filled" color="info" className="alert-justify-content-start">
								為保障您的個人資料與交易安全，僅在您完成所有終止流程及條款後，智能系統將正式終止。
								<br />
								我們將於終止生效日後刪除您於系統中的付款資訊。
								<br />
								若您日後有意重新租用智能系統，需重新填寫申請並提供付款方式。
							</Alert>
						</Grid>
						<Grid item xs={12}>
							<Alert severity="success" variant="filled" color="success" className="alert-justify-content-start">
								感謝您一路以來對 Curves 智能系統的支持。
								<br />
								希望在這段期間，科技陪伴讓您更了解自己的身體、建立起持續運動的習慣。
								<br />
								不論未來是否繼續使用，Curves 永遠在這裡陪您前行。
							</Alert>
						</Grid>
						<Grid item xs={12}>
							<CheckboxField ref={isAllowCheckedRef} required>
								<CheckboxField.Checkbox label="本人已瞭解、詳讀智能系統會員權終止條款，並同意遵守。" value={true} />
							</CheckboxField>
						</Grid>
						<Grid item xs={6}>
							<SignBox ref={signRef} required />
						</Grid>
					</FieldGroup>
				</Box>
			</Card.CardContent>
		</React.Fragment>
	);
});

// 會員資訊
export const ContractQuitMemberInfo = ({ sourceData }) => {
	const { memberName, memberNo, memberIdentityNumber, address, cellPhone, mail } = sourceData || {};
	return (
		<React.Fragment>
			<Card.CardHeader title="會員資料" className="bg-light text-center" />
			<Card.CardContent className="px-4 py-5">
				<Grid spacing={3} container>
					<Grid xs={6} item>
						<ReadTextField label="會員">{memberName}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="會員編號">{memberNo}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="身份證字號">{memberIdentityNumber}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="地址">{address}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="行動電話號碼">{cellPhone}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label="電子信箱Email">{mail}</ReadTextField>
					</Grid>
				</Grid>
			</Card.CardContent>
		</React.Fragment>
	);
};

// Main Content
const SmartFitnessBandContractQuitPage = props => {
	/** @type {{ contractID: string } */
	const { contractID: _contractID } = useParams();
	const showPDF = useShowPDF();
	const history = useHistory();
	const [isMounted, setIsMounted] = useState(false);
	const [sourceData, setSourceData] = useState(null);
	const contractQuitFormFieldsRef = useRef(null);
	const contractQuitPayableInfoRef = useRef(null);
	const { getDeviceOrderQuitApi, postDeviceOrderQuitApi } = useDeviceOrderApi();
	const contractID = Number(_contractID);

	const getParams = () => {
		const formFields = contractQuitFormFieldsRef.current.getResult();
		const payableInfo = contractQuitPayableInfoRef.current.getResult();
		return {
			memberID: sourceData.memberID,
			...formFields,
			...payableInfo,
		};
	};

	const handleSubmit = () => {
		if (!refIsRequiredError(contractQuitFormFieldsRef, contractQuitPayableInfoRef)) {
			const params = getParams();
			doDeviceOrderQuitApi(params);
		}
	};

	const doDeviceOrderQuitApi = async params => {
		const resp = await postDeviceOrderQuitApi(params, contractID);
		if (resp) {
			if (!resp.isError) {
				const { completedPath } = resp;
				const routerPath = CLUB_MEMBERS_ROUTER.SMARTFITNESSBAND.GET_TABS_PATH("overview");
				showPDF({
					open: true,
					pdfUrl: completedPath,
				});
				history.push(routerPath);
			}
		}
	};

	const initDataApi = async () => {
		const resp = await getDeviceOrderQuitApi(contractID);
		if (resp) {
			setSourceData(resp);
			setIsMounted(true);
		} else {
			const routerPath = CLUB_MEMBERS_ROUTER.SMARTFITNESSBAND.GET_TABS_PATH("overview");
			history.push(routerPath);
		}
	};

	useEffect(
		() => {
			if (contractID) {
				initDataApi();
			}
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<PageLayout>
			<PageLayout.Header
				title="智能系統終止申請書"
				actions={[
					{
						label: "返回總覽",
						as: NavLink,
						to: CLUB_MEMBERS_ROUTER.SMARTFITNESSBAND.GET_TABS_PATH("overview"),
					},
				]}
			/>
			<Form>
				{isMounted && (
					<Card>
						<ContractQuitMemberInfo sourceData={sourceData} />
						<ContractQuitPlan sourceData={sourceData} />
						<ContractQuitFormFields ref={contractQuitFormFieldsRef} sourceData={sourceData} />
						<ContractQuitPayableInfo ref={contractQuitPayableInfoRef} sourceData={sourceData} />
					</Card>
				)}
				<Card>
					<Card.CardContent>
						<Box className="btn-group text-center my-2">
							<Button onClick={handleSubmit} name="create" variant="contained" color="secondary" className="btn-minWidth">
								確認送出
							</Button>
						</Box>
						<Typography variant="body2" className="text-center mb-0 px-2 font-color-3 my-4">
							終止申請書2025
						</Typography>
					</Card.CardContent>
				</Card>
			</Form>
		</PageLayout>
	);
};

export default SmartFitnessBandContractQuitPage;
